import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { highTechStyles } from './cartographieStyles';

const API = process.env.REACT_APP_BACKEND_URL;

export function useCartographieGlobale(getAuthHeader) {
  const [loading, setLoading] = useState(true);
  const [geoSyncing, setGeoSyncing] = useState(false);
  const [geoSyncResult, setGeoSyncResult] = useState(null);
  const [mapStats, setMapStats] = useState(null);
  const [selectedView, setSelectedView] = useState('all');
  const [timeRange, setTimeRange] = useState('all');
  const [territoireFilter, setTerritoireFilter] = useState('all');
  const [territoires, setTerritoires] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showLegendModal, setShowLegendModal] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [customLegends, setCustomLegends] = useState([
    { id: 1, color: '#ef4444', label: 'Dommages déclarés', active: true },
    { id: 2, color: '#a855f7', label: 'Travaux de réparation', active: true },
    { id: 3, color: '#3b82f6', label: 'Experts actifs', active: true },
    { id: 4, color: '#f97316', label: 'Chantiers RCDS', active: true },
    { id: 5, color: '#22c55e', label: 'COOPER\'S partenaires', active: true },
  ]);
  const [customFilters, setCustomFilters] = useState([
    { id: 1, name: 'Urgences', criteria: 'priority:high', active: false },
    { id: 2, name: 'Cette semaine', criteria: 'date:week', active: false },
    { id: 3, name: 'Mon territoire', criteria: 'territory:mine', active: false },
  ]);
  const [newLegend, setNewLegend] = useState({ color: '#3b82f6', label: '' });
  const [newFilter, setNewFilter] = useState({ name: '', criteria: '' });
  const [searchTerritory, setSearchTerritory] = useState('');

  const loadData = async () => {
    try {
      setLoading(true);
      const [statsRes, territoriesRes, activityRes] = await Promise.all([
        axios.get(`${API}/api/map/stats`, { headers: getAuthHeader() }).catch(() => ({ data: {} })),
        axios.get(`${API}/api/territoires`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
        axios.get(`${API}/api/map/recent-activity?limit=8`, { headers: getAuthHeader() }).catch(() => ({ data: [] }))
      ]);
      setMapStats(statsRes.data || {});
      setTerritoires(territoriesRes.data || []);
      setRecentActivity(Array.isArray(activityRes.data) ? activityRes.data : []);
    } catch {
      toast.error('Erreur lors du chargement des données cartographiques');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    const styleEl = document.createElement('style');
    styleEl.textContent = highTechStyles;
    document.head.appendChild(styleEl);
    return () => styleEl.remove();
    // eslint-disable-next-line
  }, []);

  const handleSyncGPS = async () => {
    if (geoSyncing) return;
    if (!window.confirm(
      "Lancer la synchronisation GPS pour ~200 utilisateurs ?\n\n" +
      "Cette opération interroge OpenStreetMap (Nominatim) à raison de ~1 requête/seconde, " +
      "donc elle peut prendre jusqu'à 3 minutes. La carte sera rechargée automatiquement à la fin."
    )) return;
    setGeoSyncing(true);
    setGeoSyncResult(null);
    const toastId = toast.loading('Synchronisation GPS en cours… (~2-3 min)');
    try {
      const res = await axios.post(
        `${API}/api/map/backfill-geocoding?collection=all&max_items=200`,
        null,
        { headers: getAuthHeader(), timeout: 250000 },
      );
      const { processed = 0, geocoded = 0, failed = 0 } = res.data || {};
      setGeoSyncResult({ processed, geocoded, failed });
      toast.success(
        `Synchronisation terminée : ${geocoded} positions GPS ajoutées (${failed} échecs sur ${processed} traités).`,
        { id: toastId, duration: 8000 },
      );
      await loadData();
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || 'Erreur réseau';
      toast.error(`Synchronisation GPS échouée : ${msg}`, { id: toastId, duration: 6000 });
    } finally {
      setGeoSyncing(false);
    }
  };

  const addCustomLegend = () => {
    if (!newLegend.label.trim()) {
      toast.error('Veuillez entrer un libellé');
      return;
    }
    setCustomLegends([...customLegends, { id: Date.now(), color: newLegend.color, label: newLegend.label, active: true }]);
    setNewLegend({ color: '#3b82f6', label: '' });
    toast.success('Légende ajoutée');
  };

  const removeLegend = (id) => {
    setCustomLegends(customLegends.filter(l => l.id !== id));
    toast.success('Légende supprimée');
  };

  const toggleLegend = (id) => {
    setCustomLegends(customLegends.map(l => (l.id === id ? { ...l, active: !l.active } : l)));
  };

  const addCustomFilter = () => {
    if (!newFilter.name.trim() || !newFilter.criteria.trim()) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    setCustomFilters([...customFilters, { id: Date.now(), name: newFilter.name, criteria: newFilter.criteria, active: false }]);
    setNewFilter({ name: '', criteria: '' });
    toast.success('Filtre créé');
  };

  const removeFilter = (id) => {
    setCustomFilters(customFilters.filter(f => f.id !== id));
    toast.success('Filtre supprimé');
  };

  const toggleFilter = (id) => {
    setCustomFilters(customFilters.map(f => (f.id === id ? { ...f, active: !f.active } : f)));
  };

  const filteredTerritoires = useMemo(() => {
    const defaultTerritoires = [
      { id: 1, nom: 'Île-de-France', code: 'IDF', dommages: 12, reparations: 8, experts: 5, rcds: 45, coopers: 3, population: '12.2M', superficie: '12,012 km²' },
      { id: 2, nom: 'Provence-Alpes-Côte d\'Azur', code: 'PACA', dommages: 8, reparations: 5, experts: 3, rcds: 28, coopers: 2, population: '5.1M', superficie: '31,400 km²' },
      { id: 3, nom: 'Auvergne-Rhône-Alpes', code: 'ARA', dommages: 6, reparations: 4, experts: 4, rcds: 32, coopers: 2, population: '8.0M', superficie: '69,711 km²' },
      { id: 4, nom: 'Nouvelle-Aquitaine', code: 'NAQ', dommages: 4, reparations: 2, experts: 2, rcds: 18, coopers: 1, population: '6.0M', superficie: '84,061 km²' },
      { id: 5, nom: 'Occitanie', code: 'OCC', dommages: 5, reparations: 3, experts: 2, rcds: 22, coopers: 2, population: '5.9M', superficie: '72,724 km²' },
      { id: 6, nom: 'Hauts-de-France', code: 'HDF', dommages: 7, reparations: 4, experts: 3, rcds: 25, coopers: 2, population: '6.0M', superficie: '31,813 km²' },
      { id: 7, nom: 'Grand Est', code: 'GES', dommages: 4, reparations: 3, experts: 2, rcds: 20, coopers: 1, population: '5.5M', superficie: '57,441 km²' },
      { id: 8, nom: 'Pays de la Loire', code: 'PDL', dommages: 3, reparations: 2, experts: 2, rcds: 15, coopers: 1, population: '3.8M', superficie: '32,082 km²' },
    ];
    const data = territoires.length > 0 ? territoires : defaultTerritoires;
    if (!searchTerritory.trim()) return data;
    return data.filter(t =>
      t.nom.toLowerCase().includes(searchTerritory.toLowerCase()) ||
      (t.code && t.code.toLowerCase().includes(searchTerritory.toLowerCase()))
    );
  }, [territoires, searchTerritory]);

  const totalStats = useMemo(() => {
    return filteredTerritoires.reduce((acc, t) => ({
      dommages: acc.dommages + (t.dommages || 0),
      reparations: acc.reparations + (t.reparations || 0),
      experts: acc.experts + (t.experts || 0),
      rcds: acc.rcds + (t.rcds || 0),
      coopers: acc.coopers + (t.coopers || 0),
    }), { dommages: 0, reparations: 0, experts: 0, rcds: 0, coopers: 0 });
  }, [filteredTerritoires]);

  const openFullscreenMap = () => setIsFullscreen(true);

  return {
    loading, geoSyncing, geoSyncResult, mapStats, selectedView, setSelectedView,
    timeRange, setTimeRange, territoireFilter, setTerritoireFilter, recentActivity,
    isFullscreen, setIsFullscreen, showLegendModal, setShowLegendModal,
    showFilterModal, setShowFilterModal, customLegends, customFilters,
    newLegend, setNewLegend, newFilter, setNewFilter, searchTerritory, setSearchTerritory,
    handleSyncGPS, addCustomLegend, removeLegend, toggleLegend,
    addCustomFilter, removeFilter, toggleFilter,
    filteredTerritoires, totalStats, openFullscreenMap, loadData,
  };
}
